// ward-grouping.js — PURE ward/bed grouping model (no I/O, no Medplum imports).
//
// The ward-configuration feature (owner ask 2026-07-10): a hospital has multiple
// ward TYPES — general wards, private rooms, ICU, etc. This module holds the
// FHIR-correct model of a "ward type" and the pure read-shaping that groups the
// flat bed list (as returned by ipd-live.listWardBeds) into ward sections the
// WardBoard + occupancy widgets render. It imports nothing so it is trivially
// unit-testable under BOTH `node --test` (tests/ipd2) and vitest.
//
// FHIR model (contract §2 stays intact — occupancy is DERIVED, never written):
//   • Ward  = Location, physicalType 'wa', identifier https://healther.io/ward|<slug>,
//     and the WARD TYPE on Location.type[].coding{system:WARD_TYPE_SYSTEM, code}.
//     (Location.type is FHIR's "type of function performed at the location" — the
//     correct home for "this ward is an ICU / private / general ward". physicalType
//     stays 'wa' for every ward; the clinical category rides on `type`.)
//   • Bed   = Location, physicalType 'bd', partOf the ward Location.
// A ward with no `type` reads as 'general'. A bed with no partOf ward groups under
// the synthetic default "General Ward" so pre-migration flat beds still render.

export const WARD_TYPE_SYSTEM = 'https://healther.io/ward-type';
export const WARD_ID_SYSTEM = 'https://healther.io/ward';
export const WARD_PHYS_SYSTEM = 'http://terminology.hl7.org/CodeSystem/location-physical-type';

// FLOORS (wave-B) — a floor is a Location physicalType 'lvl' (HL7 "Level"), a ward
// hangs off it via ward.partOf → floor (hierarchy: floor → ward → bed). Floors are
// OPTIONAL: a hospital with no floor Locations reads exactly as before (every group
// carries floor:null; no synthetic floor is ever invented). The FHIR-correct home
// for the human "Floor 1 / Floor 2" ordinal is a small extension carrying the level
// integer — Location has no native storey field — while the display name is the
// Location.name.
export const FLOOR_ID_SYSTEM = 'https://healther.io/floor';
export const FLOOR_LEVEL_EXT = 'https://healther.io/fhir/floor-level';
export const FLOOR_PHYS_CODE = 'lvl';

// The closed set of ward categories the admin UI offers (contract badges).
export const WARD_TYPES = [
  { code: 'general',   label: 'General ward' },
  { code: 'private',   label: 'Private rooms' },
  { code: 'icu',       label: 'ICU' },
  { code: 'hdu',       label: 'HDU' },
  { code: 'maternity', label: 'Maternity' },
  { code: 'other',     label: 'Other' },
];
const WARD_TYPE_ORDER = WARD_TYPES.reduce((m, t, i) => { m[t.code] = i; return m; }, {});
const WARD_TYPE_LABEL = WARD_TYPES.reduce((m, t) => { m[t.code] = t.label; return m; }, {});

// The key + display for the synthetic group that collects flat (partOf-less) beds.
export const DEFAULT_WARD_KEY = '__default__';
const DEFAULT_WARD_NAME = 'General Ward';

/** wardTypeLabel(code) → human label; unknown/empty → 'General ward'. */
export function wardTypeLabel(code) {
  return WARD_TYPE_LABEL[String(code || '').toLowerCase()] || WARD_TYPE_LABEL.general;
}

/** wardTypeOf(location) → the ward-type code stored on Location.type; 'general'
 *  when none is stamped. Tolerant of a raw FHIR Location or a shaped {type}. */
export function wardTypeOf(location) {
  if (!location) return 'general';
  // Already-shaped projection.
  if (typeof location.type === 'string') return normType(location.type);
  const coding = []
    .concat(...((location.type || []).map((t) => (t && t.coding) || [])));
  const hit = coding.find((c) => c && c.system === WARD_TYPE_SYSTEM && c.code);
  return hit ? normType(hit.code) : 'general';
}
function normType(code) {
  const c = String(code || '').toLowerCase();
  return WARD_TYPE_LABEL[c] ? c : 'other';
}

/** The Location.type value stamping a ward category (for the admin write path). */
export function wardTypeCodeableConcept(code) {
  const c = normType(code);
  return { coding: [{ system: WARD_TYPE_SYSTEM, code: c, display: wardTypeLabel(c) }] };
}

// A ward projection {id,name,type,typeLabel,key} from a raw ward Location — the
// shape ipd-live hangs on each bed as `bed.ward`, and the admin loader reuses.
export function wardProjection(location) {
  if (!location) return null;
  const type = wardTypeOf(location);
  return {
    id: location.id,
    name: location.name || DEFAULT_WARD_NAME,
    type,
    typeLabel: wardTypeLabel(type),
    key: location.id || DEFAULT_WARD_KEY,
    location,
  };
}

// ── floors ────────────────────────────────────────────────────────────────────
/** floorLevelOf(location) → the ordinal level integer stored on the floor-level
 *  extension, or null when absent. Tolerant of a raw Location or a shaped {level}. */
export function floorLevelOf(location) {
  if (!location) return null;
  if (typeof location.level === 'number') return location.level;
  const hit = (location.extension || []).find((e) => e && e.url === FLOOR_LEVEL_EXT);
  const v = hit && (hit.valueInteger != null ? hit.valueInteger : hit.valueDecimal);
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** floorProjection(location) → {id,name,level} from a raw floor Location; null in →
 *  null out. The shape the WardBoard floor switcher + occupancy breakdown read. */
export function floorProjection(location) {
  if (!location || !location.id) return null;
  return { id: location.id, name: location.name || 'Floor', level: floorLevelOf(location) };
}

/** The floor-level extension array for the admin write path (empty when no level). */
export function floorLevelExtension(level) {
  const n = Number(level);
  return Number.isFinite(n) ? [{ url: FLOOR_LEVEL_EXT, valueInteger: Math.round(n) }] : [];
}

// A stable id from a ref/string (pure — the module still imports nothing).
function refIdOf(ref) {
  const r = (ref && ref.reference) || (typeof ref === 'string' ? ref : '');
  return r ? r.split('/')[1] : null;
}

// Comparable sort key for a floor projection: lower levels first, a floor WITHOUT a
// level after the numbered ones, wards with no floor at all last (before the flat
// default group). Kept in one place so the board + shaper agree on ordering.
export function floorSortRank(floor) {
  if (!floor) return Number.MAX_SAFE_INTEGER - 1; // no floor → after every real floor
  return typeof floor.level === 'number' ? floor.level : Number.MAX_SAFE_INTEGER - 2;
}

/**
 * shapeWardGroups({ wards, beds, floors }) → [{ id, key, name, type, typeLabel,
 *   floor:{id,name,level}|null, beds, total, occupied }]  — the pure read-shaping
 *   the WardBoard groups on.
 *
 * `wards` = raw ward Location[]; `beds` = the joined bed list from listWardBeds
 * (each { ward:{id,type,…}|null, encounter:…|null, … }); `floors` = raw floor
 * Location[] (physicalType 'lvl') — OPTIONAL. Each ward resolves its floor via the
 * raw ward Location.partOf → floor; a ward on no floor (or a hospital with no floors
 * at all) carries floor:null and the board renders exactly as before. Beds whose
 * ward is null (flat, pre-migration) collect under ONE synthetic default group so
 * nothing is ever dropped. Groups with zero beds are omitted. Sorted by FLOOR (level
 * ascending, floor-less wards after) then ward-type order then name; the default
 * group always last. Occupancy is DERIVED from bed.encounter — never read off the
 * Location.
 */
export function shapeWardGroups({ wards = [], beds = [], floors = [] } = {}) {
  const wardById = new Map();
  for (const w of wards) { if (w && w.id) wardById.set(w.id, w); }
  const floorById = new Map();
  for (const f of floors) { if (f && f.id) floorById.set(f.id, floorProjection(f)); }

  // The floor projection for a ward id, resolved through the raw ward Location's
  // partOf → floor. null when the ward is on no floor / no floors exist.
  const floorForWard = (wardId) => {
    const loc = wardById.get(wardId);
    const fid = loc ? refIdOf(loc.partOf) : null;
    return (fid && floorById.get(fid)) || null;
  };

  const groups = new Map(); // key → group
  const ensureGroup = (key, seed) => {
    let g = groups.get(key);
    if (!g) { g = { ...seed, key, beds: [], total: 0, occupied: 0 }; groups.set(key, g); }
    return g;
  };

  for (const bed of beds) {
    const wardRef = bed && bed.ward;
    let g;
    if (wardRef && wardRef.id) {
      const loc = wardById.get(wardRef.id);
      const type = wardRef.type || (loc ? wardTypeOf(loc) : 'general');
      g = ensureGroup(wardRef.id, {
        id: wardRef.id,
        name: wardRef.name || (loc && loc.name) || DEFAULT_WARD_NAME,
        type,
        typeLabel: wardTypeLabel(type),
        // Prefer the floor already carried on the bed's ward projection (ipd-live
        // stamps it); fall back to resolving through the raw ward Location.partOf.
        floor: (wardRef.floor && wardRef.floor.id ? wardRef.floor : floorForWard(wardRef.id)) || null,
      });
    } else {
      g = ensureGroup(DEFAULT_WARD_KEY, {
        id: null, name: DEFAULT_WARD_NAME, type: 'general', typeLabel: wardTypeLabel('general'), floor: null,
      });
    }
    g.beds.push(bed);
    g.total += 1;
    if (bed && bed.encounter) g.occupied += 1;
  }

  return [...groups.values()].sort((a, b) => {
    // Default (flat) group always sorts last.
    if (a.key === DEFAULT_WARD_KEY) return 1;
    if (b.key === DEFAULT_WARD_KEY) return -1;
    const fr = floorSortRank(a.floor) - floorSortRank(b.floor);
    if (fr !== 0) return fr;
    const to = (WARD_TYPE_ORDER[a.type] ?? 99) - (WARD_TYPE_ORDER[b.type] ?? 99);
    if (to !== 0) return to;
    return String(a.name).localeCompare(String(b.name), undefined, { numeric: true });
  });
}

/**
 * floorGroupsOf(groups) → [{ floor:{id,name,level}|null, key, groups:[wardGroup] }]
 * A pure re-shaping of shapeWardGroups' output into per-floor buckets for the board's
 * floor switcher + the occupancy widget's floor breakdown. Floor-less ward groups
 * (including the flat default) collect under a single { floor:null } bucket, listed
 * last. Buckets are ordered by floor level (ascending). Single-floor / no-floor
 * hospitals produce ONE bucket → the caller renders no switcher.
 */
export function floorGroupsOf(groups = []) {
  const buckets = new Map(); // floorId|'__none__' → bucket
  for (const g of groups) {
    const floor = g.floor && g.floor.id ? g.floor : null;
    const key = floor ? floor.id : '__none__';
    let b = buckets.get(key);
    if (!b) { b = { key, floor, groups: [] }; buckets.set(key, b); }
    b.groups.push(g);
  }
  return [...buckets.values()].sort((a, b) => floorSortRank(a.floor) - floorSortRank(b.floor));
}
